import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { PromotionsSelector } from '@/components/form/offer/promotions-selector';
import {
  useRecurringDiscountsSettings,
  useUpdateRecurringDiscountsSettings,
  type RecurringDiscountsSettings,
} from '@/lib/api/recurringDiscounts';

type Draft = { blockCoupons: boolean; exemptPromotionIds: string[] };

/** Absent config means the switch is off and no promotion is exempt — today's behaviour. */
const readAll = (settings: RecurringDiscountsSettings | undefined): Draft => ({
  blockCoupons: settings?.blockCoupons ?? false,
  exemptPromotionIds: settings?.exemptPromotionIds ?? [],
});

const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

const RecurringDiscountsCard = ({ storeId }: { storeId: string }) => {
  const { t } = useTranslation('stores');
  const { toast } = useToast();

  const { data: settings, isLoading } = useRecurringDiscountsSettings(storeId);
  const updateSettings = useUpdateRecurringDiscountsSettings();

  // `null` until the merchant touches something, so the card follows the server value while it
  // loads and again after a save, with no effect to keep the two in step.
  const [draft, setDraft] = useState<Draft | null>(null);

  const saved = readAll(settings);
  const current = draft ?? saved;
  const hasChanges =
    current.blockCoupons !== saved.blockCoupons ||
    !sameIds(current.exemptPromotionIds, saved.exemptPromotionIds);

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({ storeId, settings: current });
      setDraft(null);
      toast({
        title: t('recurringDiscounts.successTitle'),
        description: t('recurringDiscounts.successDescription'),
      });
    } catch (error) {
      toast({
        title: t('recurringDiscounts.errorTitle'),
        description:
          error instanceof Error ? error.message : t('recurringDiscounts.errorDescription'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <CardTitle>{t('recurringDiscounts.card.title')}</CardTitle>
          <CardDescription>{t('recurringDiscounts.card.description')}</CardDescription>
        </div>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateSettings.isPending || isLoading}
          className="flex-shrink-0 gap-2"
        >
          <Save className="h-4 w-4" />
          {updateSettings.isPending ? t('recurringDiscounts.saving') : t('recurringDiscounts.save')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium">{t('recurringDiscounts.toggle.label')}</p>
                <p className="text-muted-foreground text-sm">
                  {t('recurringDiscounts.toggle.description')}
                </p>
              </div>
              <Switch
                id="recurring-discounts-block-coupons"
                checked={current.blockCoupons}
                onCheckedChange={(checked) =>
                  setDraft((prev) => ({ ...(prev ?? saved), blockCoupons: checked }))
                }
              />
            </div>
            {current.blockCoupons && (
              <div className="border-t pt-4">
                <p className="font-medium">{t('recurringDiscounts.exemptions.title')}</p>
                <p className="text-muted-foreground mb-3 text-sm">
                  {t('recurringDiscounts.exemptions.description')}
                </p>
                <PromotionsSelector
                  storeId={storeId}
                  promotionIds={current.exemptPromotionIds}
                  onPromotionIdsChange={(ids) =>
                    setDraft((prev) => ({ ...(prev ?? saved), exemptPromotionIds: ids }))
                  }
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const RecurringDiscountsSettingsPageInner = () => {
  const { t } = useTranslation('stores');
  const { id: storeId } = useParams<{ id: string }>();

  if (!storeId) return null;

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">{t('recurringDiscounts.title')}</h1>
        <p className="text-muted-foreground text-sm">{t('recurringDiscounts.description')}</p>
      </div>

      <RecurringDiscountsCard storeId={storeId} />
    </div>
  );
};

export const RecurringDiscountsSettingsPage = withRequiredPermission(
  RecurringDiscountsSettingsPageInner,
  PERMISSIONS.STORES_MANAGE,
);
